import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiMutate } from "@/lib/api";

export interface KbArticleSummary {
  id: number;
  slug: string;
  category: string;
  title: string;
  tags: string;
  related_products: string;
  created_at: string;
  updated_at: string;
  preview?: string;
}

export interface KbArticle extends KbArticleSummary {
  content: string;
}

interface KbListResponse {
  articles: KbArticleSummary[];
  categories: string[];
}

export function useKbArticles(category?: string, preview = false) {
  return useQuery({
    queryKey: ["kb-articles", category, preview],
    queryFn: () => {
      const params: Record<string, string> = {};
      if (category) params.category = category;
      if (preview) params.preview = "true";
      return apiFetch<KbListResponse>("/api/kb", Object.keys(params).length ? params : undefined);
    },
  });
}

export function useKbArticle(id: number | null) {
  return useQuery({
    queryKey: ["kb-article", id],
    queryFn: () => apiFetch<KbArticle>(`/api/kb/${id}`),
    enabled: id !== null,
  });
}

export function useCreateKbArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      slug: string;
      category: string;
      title: string;
      content: string;
      tags?: string;
      related_products?: string;
    }) => apiMutate<{ id: number }>("/api/kb", "POST", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kb-articles"] }),
  });
}

export function useUpdateKbArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number;
      slug?: string;
      category?: string;
      title?: string;
      content?: string;
      tags?: string;
      related_products?: string;
    }) => apiMutate<{ ok: boolean }>(`/api/kb/${id}`, "PUT", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kb-articles"] });
      qc.invalidateQueries({ queryKey: ["kb-article"] });
    },
  });
}

export function useDeleteKbArticle() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiMutate<{ ok: boolean }>(`/api/kb/${id}`, "DELETE"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["kb-articles"] }),
  });
}
